import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import {
  ArrowDownLeft, ArrowUpRight, ChevronLeft, Download,
  Loader2, Mic,
} from "lucide-react";
import { api, toastError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";

function formatPhone(p) {
  try {
    const n = parsePhoneNumberFromString(p);
    return n ? n.formatInternational() : p;
  } catch { return p; }
}
function durationStr(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m ? `${m}m ${r}s` : `${r}s`;
}

export default function CallDetail() {
  const { job_id } = useParams();
  const { token } = useAuth();
  const [call, setCall] = useState(null);
  const [audioBlobUrl, setAudioBlobUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/calls/${job_id}`);
        setCall(r.data);
      } catch (err) {
        toastError(err, "Failed to load call");
      }
    })();
  }, [job_id]);

  // Fetch the recording as a blob so we can use it with <audio src> without
  // putting the JWT in the URL bar.
  useEffect(() => {
    if (!call?.result?.recording_url) return;
    let revoked = false;
    setAudioLoading(true);
    (async () => {
      try {
        // recording_url already includes the "/api/..." prefix; the axios
        // baseURL adds another "/api" so strip it before the call.
        const relPath = call.result.recording_url.startsWith("/api/")
          ? call.result.recording_url.slice(4)
          : call.result.recording_url;
        const r = await api.get(relPath, { responseType: "blob" });
        if (revoked) return;
        const url = URL.createObjectURL(r.data);
        setAudioBlobUrl(url);
      } catch (err) {
        // 404 is fine — recording may have been retention-swept.
        if (err?.response?.status !== 404) {
          toastError(err, "Could not load recording");
        }
      } finally {
        setAudioLoading(false);
      }
    })();
    return () => {
      revoked = true;
      if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.result?.recording_url]);

  if (!call) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(call, null, 2)], { type: "application/json" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `${call.id}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(u), 1000);
  };

  return (
    <div className="space-y-8" data-testid="call-detail-page">
      {/* Header */}
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/calls" data-testid="call-back">
            <ChevronLeft className="h-4 w-4" /> Calls
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3 mt-2">
          {call.direction === "inbound"
            ? <ArrowDownLeft className="h-5 w-5 text-blue-500" />
            : <ArrowUpRight className="h-5 w-5 text-emerald-500" />}
          <h1 className="text-2xl font-semibold tracking-tight font-mono" data-testid="call-phone">
            {formatPhone(call.contact_phone)}
          </h1>
          <Badge variant="outline" data-testid="call-status">{call.status}</Badge>
          <span className="text-sm text-muted-foreground">
            {call.agent?.name} · {call.campaign?.name || "—"} · {call.trunk?.name}
          </span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={downloadJson}>
            <Download className="h-4 w-4" /> JSON
          </Button>
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-4 text-sm">
          <Field label="Started" value={call.result?.started_at ? new Date(call.result.started_at).toLocaleString() : "—"} />
          <Field label="Answered" value={call.result?.answered_at ? new Date(call.result.answered_at).toLocaleString() : "—"} />
          <Field label="Ended" value={call.result?.ended_at ? new Date(call.result.ended_at).toLocaleString() : "—"} />
          <Field label="Duration" value={durationStr(call.result?.duration_s)} />
          <Field
            label="Estimated cost"
            value={call.result?.cost_estimate_usd != null
              ? `$${Number(call.result.cost_estimate_usd).toFixed(3)}`
              : "—"}
            testid="call-cost"
          />
          <Field label="Hangup cause" value={call.result?.hangup_cause || "—"} />
          <Field label="SIP cause" value={call.result?.sip_cause || "—"} />
          <Field label="Attempt #" value={call.attempt_number} />
          <Field label="Direction" value={call.direction} />
        </div>
      </div>

      {/* Recording player */}
      <Section title="Recording" testid="call-recording">
        {audioLoading ? (
          <div className="text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading recording…
          </div>
        ) : audioBlobUrl ? (
          <audio
            controls
            src={audioBlobUrl}
            className="w-full"
            data-testid="call-audio"
          />
        ) : (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Mic className="h-4 w-4" /> No recording available for this call.
          </div>
        )}
      </Section>

      {/* Transcript */}
      <Section title="Transcript" testid="call-transcript">
        {call.transcripts.length === 0 ? (
          <div className="text-sm text-muted-foreground">No transcript captured for this call.</div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
            {call.transcripts.map((t, i) => (
              <div
                key={i}
                className={`flex ${t.role === "agent" ? "justify-start" : "justify-end"}`}
              >
                <div
                  title={new Date(t.ts).toLocaleString()}
                  className={
                    "max-w-[80%] rounded-lg px-3 py-2 text-sm " +
                    (t.role === "agent"
                      ? "bg-secondary text-secondary-foreground"
                      : "bg-primary text-primary-foreground")
                  }
                  data-testid={`call-bubble-${i}`}
                >
                  {t.text}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Latency chart */}
      <Section title="Per-turn latency (ms)" testid="call-latency">
        <div className="flex items-end gap-1 h-32" data-testid="call-latency-bars">
          {call.latency_turns.length === 0 ? (
            <div className="text-sm text-muted-foreground self-center">No latency metrics captured.</div>
          ) : (
            call.latency_turns.map((t) => {
              const v = t.e2e_ms || 0;
              const max = Math.max(...call.latency_turns.map((x) => x.e2e_ms || 0), 1);
              const h = Math.max(2, (v / max) * 120);
              const color = v > 700 ? "bg-destructive" : v > 500 ? "bg-amber-500" : "bg-emerald-500";
              return (
                <div key={t.turn} className="flex flex-col items-center gap-1">
                  <div className={`w-6 ${color} rounded-t`} style={{ height: h }} title={`${v} ms`} />
                  <span className="text-[10px] font-mono text-muted-foreground">{v}</span>
                </div>
              );
            })
          )}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
          <Field label="Turns" value={call.latency_summary.count} />
          <Field label="P50" value={call.latency_summary.p50_ms ?? "—"} />
          <Field label="P95" value={call.latency_summary.p95_ms ?? "—"} />
        </div>
      </Section>

      {/* Raw events */}
      <Collapsible open={eventsOpen} onOpenChange={setEventsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" size="sm" data-testid="call-events-toggle">
            {eventsOpen ? "Hide" : "Show"} raw events ({call.events.length})
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          <div className="rounded-md border border-border bg-card max-h-[400px] overflow-y-auto" data-testid="call-events-list">
            <ul className="divide-y divide-border">
              {call.events.map((ev) => (
                <li key={ev.id} className="px-3 py-2 text-xs font-mono">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline">{ev.type}</Badge>
                    <span className="text-muted-foreground">{new Date(ev.received_at).toLocaleString()}</span>
                  </div>
                  <pre className="mt-1 text-xs whitespace-pre-wrap break-all text-muted-foreground">
                    {JSON.stringify(ev.payload, null, 2)}
                  </pre>
                </li>
              ))}
            </ul>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2" data-testid={testid}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm">{value}</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="rounded-lg border border-border bg-card" data-testid={testid}>
      <div className="border-b border-border px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
